import { AssetSearchResponse } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { ApiErrorDisplay } from '@/types/apiError';
import { downloadSpreadsheet } from '@/utils/downloadUtils';
import {
  CloseOutlined,
  FileDownloadOutlined,
  FilterAlt,
  FilterListOutlined,
  InsertDriveFileOutlined,
  SearchOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { StyledList, StyledListItem } from '../AssetsPage/PopoverList';
import { RoundedPopover } from '../Popover/RoundedPopover';

import { CellRenderer } from './components/CellRenderer';
import { DataCell, DraggableRow, HeaderCell, StyledTable, TableContainer } from './components/SuitableSubstitutesTable';
import { useFilterManagement } from './hooks/useFilterManagement';
import { useRowDragDrop } from './hooks/useRowDragDrop';
import { useTableNavigation } from './hooks/useTableNavigation';
import { useTableState } from './hooks/useTableState';
import { MetadataRow } from './types/suitableSubstituteTypes';
import { getAssetValue } from './utils/getAssetValue';

export const SuitableSubstitutesTable = ({
  isLoading: _isLoading,
  error: _error,
  searchData,
  tableState,
}: {
  isLoading: boolean;
  error?: ApiErrorDisplay;
  searchData?: AssetSearchResponse;
  tableState: ReturnType<typeof useTableState>;
}) => {
  const tableContainerReference = useRef<HTMLDivElement>(null);
  const searchInputReference = useRef<HTMLInputElement>(null);

  const { assetId, searchQuery, updateSearchQuery } = useSearchFilters();
  const { asset: selectedAsset } = useGetAsset({ assetId });

  const { handlePreviousOption, handleNextOption } = useTableNavigation(tableContainerReference);

  const {
    selectedSubstitutes,
    hiddenAssetIds,
    showSearchInput,
    localSearchQuery,
    setShowSearchInput,
    setLocalSearchQuery,
    handleSubstituteToggle,
    handleHideAsset,
    handleSearchChange,
    handleClearSearch,
  } = tableState;

  const matches = useMemo(() => {
    if (!searchData?.matches) return [];

    return searchData.matches.map(match => ({
      ...match.asset,
      contentType: 'asset' as const,
      matchPercentage: 'matchPercentage' in match ? match.matchPercentage : undefined,
      forwardMatchPercentage: 'forwardMatchPercentage' in match ? match.forwardMatchPercentage : undefined,
      reverseMatchPercentage: 'reverseMatchPercentage' in match ? match.reverseMatchPercentage : undefined,
    }));
  }, [searchData]);

  const allAssets = selectedAsset ? [selectedAsset, ...matches] : matches;

  const {
    appliedFilters,
    temporaryFilters,
    filterAnchorElement,
    activeFilterField,
    filterPopoverOpen,
    handleFilterClick,
    handleFilterClose,
    handleFilterChange,
    handleApplyFilters,
    handleClearFilter,
    getFieldValues,
  } = useFilterManagement(allAssets);

  const initialMetadataRows = useMemo(() => {
    const rows: MetadataRow[] = [
      { key: 'thumbnail', label: '', isDraggable: false },
      { key: 'name', label: 'Name', isDraggable: false },
    ];

    const metadataFields = new Set<string>();

    if (searchData?.filterData?.metadata) {
      for (const metadataField of Object.keys(searchData.filterData.metadata)) {
        metadataFields.add(metadataField);
      }
    }

    if (selectedAsset?.metadata) {
      for (const metadataField of Object.keys(selectedAsset.metadata)) {
        metadataFields.add(metadataField);
      }
    }

    for (const metadataField of [...metadataFields].sort((a, b) => a.localeCompare(b))) {
      rows.push({
        key: `metadata_${metadataField}`,
        label: metadataField,
        isDraggable: true,
      });
    }

    rows.push(
      { key: 'created', label: 'Created', isDraggable: true },
      { key: 'createdBy', label: 'Created By', isDraggable: true },
      { key: 'folder', label: 'Folder', isDraggable: true }
    );

    return rows;
  }, [searchData, selectedAsset]);

  const [metadataRows, setMetadataRows] = useState(initialMetadataRows);

  const { draggedRowIndex, handleDragStart, handleDragOver, handleDrop, handleDragEnd } = useRowDragDrop(metadataRows);

  const [exportAnchorElement, setExportAnchorElement] = useState<HTMLButtonElement>();
  const exportPopoverOpen = Boolean(exportAnchorElement);
  const [selectedExportOption, setSelectedExportOption] = useState<'all' | 'selected'>('all');

  useEffect(() => {
    setSelectedExportOption(selectedSubstitutes.length > 0 ? 'selected' : 'all');
  }, [selectedSubstitutes.length]);

  useMemo(() => {
    setMetadataRows(initialMetadataRows);
  }, [initialMetadataRows]);

  const handleSearchSubmit = () => {
    updateSearchQuery(localSearchQuery);
    setShowSearchInput(false);
  };

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportAnchorElement(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorElement(undefined);
  };

  const handleExportOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value as 'all' | 'selected';
    setSelectedExportOption(newValue);
  };

  useEffect(() => {
    if (showSearchInput && searchInputReference.current) {
      searchInputReference.current.focus();
    }
  }, [showSearchInput]);

  useEffect(() => {
    setLocalSearchQuery(searchQuery || '');
  }, [searchQuery, setLocalSearchQuery]);

  const cellRendererConfig = useMemo(
    () => ({
      searchQuery: localSearchQuery,
      selectedAssetIds: selectedSubstitutes,
      onPreviousOption: handlePreviousOption,
      onNextOption: handleNextOption,
      onToggleSelection: handleSubstituteToggle,
      onHideAsset: handleHideAsset,
    }),
    [
      localSearchQuery,
      selectedSubstitutes,
      handlePreviousOption,
      handleNextOption,
      handleSubstituteToggle,
      handleHideAsset,
    ]
  );

  const filteredAssets = useMemo(() => {
    const visibleAssets = allAssets.filter(asset => !hiddenAssetIds.has(asset.id));

    if (
      Object.keys(appliedFilters).length === 0 ||
      Object.values(appliedFilters).every(filters => filters.length === 0)
    ) {
      return visibleAssets;
    }

    const matchingAssets = visibleAssets.filter(asset => {
      if ((selectedAsset && asset.id === selectedAsset.id) || selectedSubstitutes.includes(asset.id)) {
        return true;
      }

      return Object.entries(appliedFilters).every(([fieldKey, selectedValues]) => {
        if (selectedValues.length === 0) return true;
        const assetValue = getAssetValue(asset, fieldKey);
        return selectedValues.includes(assetValue);
      });
    });

    return matchingAssets;
  }, [allAssets, appliedFilters, hiddenAssetIds, selectedAsset, selectedSubstitutes]);

  const handleDownload = (format: string) => {
    const assetsToExport =
      selectedExportOption === 'selected'
        ? filteredAssets.filter(asset => asset.id === selectedAsset?.id || selectedSubstitutes.includes(asset.id))
        : filteredAssets;

    const headers = [
      '',
      ...assetsToExport.map((asset, index) => {
        if (index === 0 && selectedAsset?.id === asset.id) {
          return 'Selected asset';
        }
        const originalIndex = allAssets.findIndex(a => a.id === asset.id);
        const optionNumber = selectedAsset ? originalIndex : originalIndex + 1;
        return `Option ${optionNumber}`;
      }),
    ];

    const rows = metadataRows.map(row => [row.label, ...assetsToExport.map(asset => getAssetValue(asset, row.key))]);

    const tableData = [headers, ...rows];

    const filenameSuffix = selectedAsset?.id ? `-${selectedAsset.id}` : '';
    downloadSpreadsheet(tableData, {
      filename: `suitable-substitutes${filenameSuffix}.${format.toLowerCase()}`,
      format: format.toLowerCase() as 'csv' | 'xlsx',
    });

    handleExportClose();
  };

  return (
    <TableContainer ref={tableContainerReference}>
      <RoundedPopover
        open={filterPopoverOpen}
        anchorEl={filterAnchorElement}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ minWidth: '200px', maxHeight: '300px', overflow: 'auto' }}>
          {activeFilterField && (
            <>
              <Typography
                variant='subtitle2'
                sx={theme => ({ padding: '8px 16px', borderBottom: `1px solid ${theme.palette.divider}` })}
              >
                Filter by {metadataRows.find(r => r.key === activeFilterField)?.label || 'Field'}
              </Typography>
              <FormGroup sx={{ padding: '8px 16px', maxHeight: '200px', overflowY: 'auto' }}>
                {getFieldValues(activeFilterField)
                  .filter(value => value !== '')
                  .map(value => (
                    <FormControlLabel
                      key={value}
                      control={
                        <Checkbox
                          size='small'
                          checked={temporaryFilters[activeFilterField]?.includes(value) || false}
                          onChange={event => handleFilterChange(activeFilterField, value, event.target.checked)}
                        />
                      }
                      label={value}
                      sx={{ marginBottom: '4px' }}
                    />
                  ))}
              </FormGroup>
              <Divider />
              <Box sx={{ padding: '8px', display: 'flex', justifyContent: 'flex-end', gap: '4px' }}>
                <Button size='small' onClick={handleClearFilter}>
                  Clear
                </Button>
                <Button
                  variant='contained'
                  size='small'
                  onClick={handleApplyFilters}
                  sx={{
                    backgroundColor: 'secondary.main',
                    '&:hover': {
                      backgroundColor: 'secondary.main',
                    },
                  }}
                >
                  Apply
                </Button>
              </Box>
            </>
          )}
        </Box>
      </RoundedPopover>
      <StyledTable>
        <thead>
          <tr>
            <HeaderCell>
              <Box display='flex' justifyContent='flex-start' alignItems='center' gap={1}>
                <Button
                  variant='text'
                  size='small'
                  onClick={handleExportClick}
                  sx={theme => ({
                    outline: `1px solid ${theme.palette.divider}`,
                    minWidth: '36px',
                    minHeight: '30px',
                    backgroundColor: theme.palette.background.paper,
                    paddingRight: '12px',
                    color: theme.palette.text.secondary,
                  })}
                >
                  <FileDownloadOutlined sx={{ fontSize: '18px', margin: '0 6px 0 4px', color: 'text.secondary' }} />
                  Export
                  {selectedSubstitutes.length > 0 && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        backgroundColor: 'secondary.main',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: 600,
                        marginLeft: '6px',
                      }}
                    >
                      {1 + selectedSubstitutes.length}
                    </Box>
                  )}
                </Button>
              </Box>
              <RoundedPopover
                open={exportPopoverOpen}
                anchorEl={exportAnchorElement}
                onClose={handleExportClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <Box sx={{ minWidth: '200px' }}>
                  <RadioGroup value={selectedExportOption} onChange={handleExportOptionChange}>
                    <FormControlLabel
                      value='all'
                      control={<Radio size='small' />}
                      label={`Export All (${1 + filteredAssets.length - (selectedAsset ? 1 : 0)})`}
                      sx={{ margin: '8px 0 2px 6px' }}
                    />
                    <FormControlLabel
                      value='selected'
                      control={<Radio size='small' />}
                      label={`Export Selected (${1 + selectedSubstitutes.length})`}
                      disabled={selectedSubstitutes.length === 0}
                      sx={{ margin: '0 0 8px 6px' }}
                    />
                  </RadioGroup>
                  <Divider />
                  <StyledList>
                    {['CSV', 'XLSX'].map(format => (
                      <StyledListItem key={format} onClick={() => handleDownload(format)} sx={{ cursor: 'pointer' }}>
                        <ListItemIcon>
                          <InsertDriveFileOutlined color='action' sx={{ fontSize: '18px' }} />
                        </ListItemIcon>
                        <BodyText>{format}</BodyText>
                      </StyledListItem>
                    ))}
                  </StyledList>
                </Box>
              </RoundedPopover>
            </HeaderCell>
            {filteredAssets.map((asset, filteredIndex) => {
              const isSearchAsset = filteredIndex === 0 && !!selectedAsset;
              const originalIndex = allAssets.findIndex(a => a.id === asset.id);
              const optionNumber = selectedAsset ? originalIndex : originalIndex + 1;
              return (
                <HeaderCell key={asset.id} isSticky={isSearchAsset}>
                  {isSearchAsset ? 'Selected asset' : `Option ${optionNumber}`}
                </HeaderCell>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {metadataRows.map((row, rowIndex) => (
            <DraggableRow
              key={row.key}
              draggable={row.isDraggable}
              isDragging={draggedRowIndex === rowIndex}
              onDragStart={event => handleDragStart(event, rowIndex)}
              onDragOver={handleDragOver}
              onDrop={event => handleDrop(event, rowIndex, setMetadataRows)}
              onDragEnd={handleDragEnd}
            >
              <DataCell sx={{ verticalAlign: row.label ? 'top' : 'bottom' }}>
                <Box display='flex' justifyContent='space-between' alignItems='center' gap={2}>
                  {row.label}
                  {row.label &&
                    row.label !== 'Name' &&
                    (() => {
                      const hasAppliedFilters = appliedFilters[row.key]?.length > 0;

                      return (
                        <IconButton
                          size='small'
                          onClick={event => handleFilterClick(event, row.key)}
                          sx={{ padding: '2px' }}
                        >
                          {hasAppliedFilters ? (
                            <FilterAlt fontSize='small' sx={{ color: 'secondary.main' }} />
                          ) : (
                            <FilterListOutlined fontSize='small' sx={{ color: 'action.disabled' }} />
                          )}
                        </IconButton>
                      );
                    })()}
                  {!row.label && (
                    <Box display='flex' flexDirection='column' alignItems='flex-end'>
                      <TextField
                        inputRef={searchInputReference}
                        size='small'
                        placeholder='Find'
                        value={localSearchQuery}
                        onChange={handleSearchChange}
                        sx={theme => ({
                          width: '164px',
                          '& .MuiOutlinedInput-root': {
                            fontSize: '14px',
                            borderRadius: '6px',
                            height: '32px',
                            '& fieldset': {
                              borderColor: theme.palette.divider,
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.divider,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: theme.palette.divider,
                            },
                          },
                        })}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position='start' sx={{ marginLeft: '-6px', marginRight: '4px' }}>
                                <SearchOutlined fontSize='small' />
                              </InputAdornment>
                            ),
                            endAdornment: localSearchQuery && (
                              <InputAdornment position='end' sx={{ marginRight: '-6px' }}>
                                <IconButton size='small' onClick={handleClearSearch} sx={{ marginRight: '-4px' }}>
                                  <CloseOutlined sx={{ opacity: 0.5, fontSize: '14px' }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                        onKeyDown={event => {
                          if (event.key === 'Enter') {
                            handleSearchSubmit();
                          }
                          if (event.key === 'Escape') {
                            setShowSearchInput(false);
                            setLocalSearchQuery('');
                            updateSearchQuery('');
                          }
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </DataCell>
              {filteredAssets.map((asset, index) => {
                const isSearchAsset = index === 0 && !!selectedAsset;
                return (
                  <DataCell
                    key={`${asset.id}-${row.key}`}
                    isSticky={isSearchAsset}
                    isDragging={draggedRowIndex === rowIndex}
                    sx={{
                      color: (() => {
                        if (isSearchAsset) return;
                        return 'text.primary';
                      })(),
                    }}
                  >
                    <CellRenderer
                      asset={asset}
                      rowKey={row.key}
                      isSearchAsset={isSearchAsset}
                      config={cellRendererConfig}
                    />
                  </DataCell>
                );
              })}
            </DraggableRow>
          ))}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
};
